import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import locationStorageAdapter from '../../services/adapters/locationStorage.adapter';
import useAuthHook from '../../hooks/useAuth.hook';
import {
  checkUserRequiredPermissions,
  checkUserRequiredRole,
} from '../../utils/getUserRolesByToken';
import { UserRole } from '../../types/userRole.types';
import TooltipComponent from '../Tooltip.component';

import { Menu, MenuItem, Sidebar } from 'react-pro-sidebar';

import {
  HELP_PAGE_LINK,
  RoutesConstant,
} from '../../constants/routes.constant';
import { UserPermissions } from '../../types/userPermissions.types';
import {
  menuStyles,
  sidebarRootStyles,
  StyledAsideMenu,
  StyledCollapseContainer,
  StyledLogoContainer,
} from './Aside.styles';
import {
  isCandidatesRoute,
  LOGO_ICON_SIZE,
  menuStatusStorageKey,
} from './constants';
import {
  HomeIcon,
  PeopleIcon,
  ArchiveIcon,
  LearningIcon,
  LogoWhiteIcon,
  CalendarIcon,
  ReportIcon,
  ControlIcon,
  HelpIcon,
  MessageIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  StarIcon,
} from '../Icons';
import { IAsideTab } from './Aside.types';

const AsideComponent: React.FC = () => {
  const [collapsed, setCollapsed] = useState<boolean>(
    !locationStorageAdapter.get<boolean>(menuStatusStorageKey),
  );

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { user } = useAuthHook();

  const handleToggleSidebar = (): void => {
    setCollapsed(!collapsed);
    locationStorageAdapter.set(menuStatusStorageKey, collapsed);
  };

  const preventNavigateSameRoute = (path: string, to: string): void => {
    if (path.includes(to)) return;
    navigate(to);
  };

  const isHeadUser = checkUserRequiredRole(user.roles, [
    UserRole.HEAD,
    UserRole.DEV_HEAD,
  ]);

  const isHRUser = checkUserRequiredRole(user.roles, [
    UserRole.HR,
    UserRole.DEV_HR,
  ]);

  const asideTabs: IAsideTab[] = React.useMemo(() => {
    return [
      {
        isShown: checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_VIEW_EVENT,
        ]),
        icon: <HomeIcon />,
        link: RoutesConstant.DASHBOARD,
        title: t('Dashboard'),
        handler: () =>
          preventNavigateSameRoute(pathname, RoutesConstant.DASHBOARD),
      },
      {
        isShown: true,
        icon: <PeopleIcon />,
        link: RoutesConstant.CANDIDATES,
        isActive: isCandidatesRoute(pathname),
        title: t('Candidates'),
        handler: () =>
          !isCandidatesRoute(pathname) && navigate(RoutesConstant.CANDIDATES),
      },
      {
        isShown: isHeadUser,
        icon: <StarIcon color='currentColor' size={20} />,
        link: RoutesConstant.CANDIDATES_GROUPS.FAVOURITE,
        title: t('Favourites'),
        handler: () =>
          preventNavigateSameRoute(
            pathname,
            RoutesConstant.CANDIDATES_GROUPS.FAVOURITE,
          ),
      },
      {
        isShown: true,
        icon: <ArchiveIcon />,
        link: RoutesConstant.CANDIDATES_GROUPS.ARCHIVE,
        title: t('Archive'),
        handler: () =>
          preventNavigateSameRoute(
            pathname,
            RoutesConstant.CANDIDATES_GROUPS.ARCHIVE,
          ),
      },
      {
        isShown: true,
        icon: <LearningIcon />,
        link: RoutesConstant.INTERNSHIPS,
        title: t('Internships'),
        handler: () =>
          preventNavigateSameRoute(pathname, RoutesConstant.INTERNSHIPS),
      },
      {
        isShown: checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_VIEW_EVENT,
        ]),
        icon: <CalendarIcon />,
        link: RoutesConstant.EVENTS,
        title: t('Events'),
        handler: () =>
          preventNavigateSameRoute(pathname, RoutesConstant.EVENTS),
      },
      {
        isShown: checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_VIEW_REPORT,
          UserPermissions.CAN_VIEW_GROUPED_REPORT,
        ]) || isHRUser,
        icon: <ReportIcon />,
        link: RoutesConstant.REPORT,
        isActive: pathname.includes(RoutesConstant.REPORT),
        title: t('Reports'),
        handler: () =>
          !pathname.includes(RoutesConstant.REPORT) &&
          preventNavigateSameRoute(pathname, RoutesConstant.REPORT),
      },
      {
        isShown: checkUserRequiredRole(user.roles, [
          UserRole.SUPER_HR,
          UserRole.ADMIN,
          UserRole.DEV_ADMIN,
          UserRole.DEV_SUPER_HR,
        ]),
        icon: <ControlIcon />,
        link: RoutesConstant.CONTROL_PANEL,
        title: t('Control panel'),
        handler: () =>
          preventNavigateSameRoute(pathname, RoutesConstant.CONTROL_PANEL),
      },
      {
        isShown: checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_VIEW_CANDIDATE,
        ]),
        icon: <MessageIcon />,
        link: RoutesConstant.ASSISTANT,
        title: t('Assistant'),
        handler: () =>
          preventNavigateSameRoute(pathname, RoutesConstant.ASSISTANT),
      },
      {
        isShown: true,
        icon: <HelpIcon />,
        link: HELP_PAGE_LINK,
        title: t('Help'),
        handler: () => window.open(HELP_PAGE_LINK),
      },
    ];
  }, [user, pathname]);

  return (
    <Sidebar collapsed={collapsed} rootStyles={sidebarRootStyles}>
      <StyledLogoContainer onClick={handleToggleSidebar}>
        <LogoWhiteIcon width={LOGO_ICON_SIZE} height={LOGO_ICON_SIZE} />
      </StyledLogoContainer>
      <Menu menuItemStyles={menuStyles}>
        <StyledAsideMenu>
          {asideTabs.map((item: IAsideTab) => {
            if (!item.isShown) return null;

            return (
              <MenuItem
                active={item.isActive || pathname === item.link}
                key={item.link}
                icon={item.icon}
                onClick={item.handler}
              >
                {item.title}
                {collapsed && <TooltipComponent tooltip={item.title} />}
              </MenuItem>
            );
          })}
        </StyledAsideMenu>
      </Menu>
      <StyledCollapseContainer onClick={handleToggleSidebar}>
        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </StyledCollapseContainer>
    </Sidebar>
  );
};

export default AsideComponent;
